import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Sparkles, Shuffle } from "lucide-react";
import usePageMeta from "@/hooks/usePageMeta";
import { Squiggle, SquiggleUnderline, FloatingBlob } from "@/components/Squiggle";

// De Vonkvinder — een speelse 2-minuten ontdekking, mobile-first.
// 5 stappen, 4 route-nodes: Startpunt → Rugzak → Rem → Vonk.
// Client-side, geen dataopslag, geen "type".

const STAPPEN = [
  {
    n: 1,
    node: "Startpunt",
    vraag: "Waar begint jouw dag meestal?",
    onder: "Kies wat het dichtst bij je komt.",
    opties: [
      { k: "koffie", label: "Even koffie", img: "https://images.unsplash.com/photo-1509440159596-0249088772ff?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "buiten", label: "Naar buiten", img: "https://images.unsplash.com/photo-1723227123897-0624fe046ae8?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "bed", label: "Nog even in bed", img: "https://images.unsplash.com/photo-1531123897727-8f129e1688ce?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
    ],
  },
  {
    n: 2,
    node: "Startpunt",
    vraag: "Wat past nu bij je?",
    onder: "Er is geen goed of fout — kies wat lichter voelt.",
    opties: [
      { k: "alleen", label: "Even alleen", img: "https://images.unsplash.com/photo-1519741347686-c1e0aadf4611?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "samen", label: "Iets samen", img: "https://images.unsplash.com/photo-1543269664-56d93c1b41a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "muziek", label: "Muziek erbij", img: "https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
    ],
  },
  {
    n: 3,
    node: "Rugzak",
    vraag: "Wat draag je een beetje mee?",
    onder: "Wat zit er soms in de weg?",
    opties: [
      { k: "twijfel", label: "Twijfel", img: "https://images.unsplash.com/photo-1552058544-f2b08422138a?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "moeheid", label: "Moeheid", img: "https://images.unsplash.com/photo-1508979215434-fbb1c56aad33?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "onrust", label: "Onrust", img: "https://images.unsplash.com/photo-1518611012118-696072aa579a?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
    ],
  },
  {
    n: 4,
    node: "Rem",
    vraag: "Waar loop je vaak op vast?",
    onder: "Wat zou vandaag gewoon een klein beetje minder mogen?",
    opties: [
      { k: "moeten", label: "Alles moet", img: "https://images.unsplash.com/photo-1522163182402-834f871fd851?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "niet-weten", label: "Ik weet het niet", img: "https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "alleen-doen", label: "Alles alleen", img: "https://images.unsplash.com/photo-1502680390469-be75c86b636f?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
    ],
  },
  {
    n: 5,
    node: "Vonk",
    vraag: "Waar zou je zin in kunnen krijgen?",
    onder: "Kies iets waar je nieuwsgierig van wordt.",
    opties: [
      { k: "koken", label: "Iets koken", img: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "fietsen", label: "Fietsen", img: "https://images.unsplash.com/photo-1484627147104-f5197bcd6651?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "maken", label: "Iets maken", img: "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
      { k: "bewegen", label: "Bewegen", img: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=700" },
    ],
  },
];

const NODES = ["Startpunt", "Rugzak", "Rem", "Vonk"];

const VONKEN = [
  "Er zit iets in jou dat wéér wil bewegen. Dat is genoeg.",
  "Kleine dingen wijzen soms de grootste weg.",
  "Nieuwsgierigheid is een vorm van herstel.",
  "Je hoeft niet klaar te zijn om te beginnen.",
  "Het lichter maken begint met erkennen dat het zwaar is.",
];

function ProgressRoute({ current }) {
  const activeNodeIndex = NODES.indexOf(current);
  return (
    <div className="w-full max-w-2xl mx-auto" data-testid="vonkvinder-route">
      <div className="relative flex items-center justify-between">
        <svg className="absolute inset-x-0 top-1/2 -translate-y-1/2 w-full h-3 pointer-events-none" viewBox="0 0 400 12" preserveAspectRatio="none">
          <path d="M 6 6 Q 100 -4 200 6 T 394 6" fill="none" stroke="#1F5132" strokeOpacity="0.15" strokeWidth="2" strokeLinecap="round" />
          <motion.path
            d="M 6 6 Q 100 -4 200 6 T 394 6"
            fill="none"
            stroke="#E06B3F"
            strokeWidth="2.5"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: activeNodeIndex / (NODES.length - 1) }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          />
        </svg>
        {NODES.map((label, i) => {
          const done = i < activeNodeIndex;
          const active = i === activeNodeIndex;
          return (
            <div key={label} className="relative z-10 flex flex-col items-center">
              <motion.span
                animate={{
                  scale: active ? [1, 1.25, 1] : 1,
                  backgroundColor: done ? "#E06B3F" : active ? "#F0B98D" : "#ffffff",
                }}
                transition={{ scale: { duration: 1.4, repeat: active ? Infinity : 0 }, backgroundColor: { duration: 0.4 } }}
                className="w-5 h-5 rounded-full border-2 border-ll-ink"
              />
              <span className={`mt-3 text-[10px] uppercase tracking-[0.22em] font-medium ${active ? "text-ll-ink" : "text-ll-ink/50"}`}>
                {label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Vonkvinder() {
  usePageMeta({
    title: "Vonkvinder · LevelLeven",
    description:
      "Een speelse ontdekking van 2 minuten. Geen test, geen diagnose. Alleen kijken waar bij jou de vonk zit.",
  });

  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);

  const total = STAPPEN.length;
  const done = step >= total;
  const current = STAPPEN[step];
  const currentNode = current?.node || "Vonk";

  const vonk = useMemo(() => {
    // deterministic based on answers (client-side only)
    const seed = answers.join("|").length;
    return VONKEN[seed % VONKEN.length];
  }, [answers]);

  const kies = (k) => {
    const next = [...answers, { stap: step, keuze: k }];
    setAnswers(next);
    setStep((s) => s + 1);
  };

  const reset = () => {
    setStep(0);
    setAnswers([]);
  };

  return (
    <div className="relative min-h-screen bg-ll-cream text-ll-ink overflow-hidden">
      <FloatingBlob color="#F0B98D" size={150} className="top-24 -right-10 opacity-70" />
      <FloatingBlob color="#97B79D" size={120} className="bottom-20 -left-10 opacity-70" delay={1.5} />

      <div className="max-w-[1000px] mx-auto px-5 sm:px-10 pt-28 sm:pt-32 pb-24 relative z-10">
        {/* Header intro */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <p className="text-[10px] uppercase tracking-[0.35em] text-ll-coral">De Vonkvinder</p>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight font-light">
            Vind je{" "}
            <span className="italic text-ll-coral relative inline-block">
              vonk
              <SquiggleUnderline color="#E06B3F" offset={-2} height={16} />
            </span>.
          </h1>
          <p className="mt-4 text-ll-navy/80 max-w-md mx-auto text-base sm:text-lg leading-relaxed">
            Geen test. Geen diagnose. Vijf visuele keuzes. Twee minuten. Geen data opgeslagen.
          </p>
        </motion.div>

        {/* Progress route */}
        <div className="mb-10 sm:mb-14">
          <ProgressRoute current={currentNode} />
          <p className="mt-4 text-center text-[10px] uppercase tracking-[0.3em] text-ll-navy/60">
            {done ? "Klaar" : `Stap ${step + 1} van ${total}`}
          </p>
        </div>

        {/* Steps */}
        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.45 }}
              data-testid={`vonkvinder-stap-${step + 1}`}
            >
              <h2 className="font-heading text-3xl sm:text-4xl leading-tight text-ll-ink font-light text-center max-w-2xl mx-auto">
                {current.vraag}
              </h2>
              <p className="mt-3 text-center text-ll-navy/70 italic">{current.onder}</p>

              <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
                {current.opties.map((o, i) => (
                  <motion.button
                    key={o.k}
                    type="button"
                    onClick={() => kies(o.k)}
                    data-testid={`vonkvinder-optie-${o.k}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.08, duration: 0.5 }}
                    whileHover={{ y: -6, scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="group relative overflow-hidden rounded-3xl aspect-[4/5] sm:aspect-[3/4] border border-ll-ink/10 bg-white text-left"
                  >
                    <img
                      src={o.img}
                      alt=""
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-ll-ink/85 via-ll-ink/25 to-transparent" />
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      className="absolute inset-0 bg-ll-coral/25 mix-blend-multiply"
                    />
                    <div className="absolute inset-x-0 bottom-0 p-5 sm:p-6 text-ll-cream">
                      <p className="font-heading text-2xl sm:text-3xl font-light">{o.label}</p>
                    </div>
                    <span className="absolute top-4 right-4 text-[10px] uppercase tracking-[0.28em] text-ll-cream/80">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </motion.button>
                ))}
              </div>

              {step > 0 && (
                <div className="mt-8 text-center">
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    className="text-xs uppercase tracking-[0.28em] text-ll-navy/60 hover:text-ll-ink transition-colors"
                  >
                    ← Terug
                  </button>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="uitkomst"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
              data-testid="vonkvinder-uitkomst"
            >
              {/* Vonk lights up */}
              <div className="relative inline-block">
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: [0, 1.3, 1], opacity: 1 }}
                  transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
                  className="w-32 h-32 sm:w-40 sm:h-40 rounded-full mx-auto flex items-center justify-center"
                  style={{
                    background: "radial-gradient(circle at 30% 30%, #F0B98D 0%, #E06B3F 70%, #E06B3F00 100%)",
                    boxShadow: "0 0 60px #F0B98D88",
                  }}
                >
                  <Sparkles size={44} className="text-ll-ink" strokeWidth={1.4} />
                </motion.div>
                <motion.span
                  animate={{ scale: [1, 1.4, 1], opacity: [0.3, 0, 0.3] }}
                  transition={{ duration: 2, repeat: Infinity }}
                  className="absolute inset-0 rounded-full bg-ll-yellow"
                />
              </div>

              <p className="mt-8 text-[10px] uppercase tracking-[0.35em] text-ll-coral">Hé, hier zit misschien een vonk...</p>
              <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight font-light max-w-2xl mx-auto">
                <span className="italic text-ll-coral">"{vonk}"</span>
              </h2>
              <p className="mt-6 text-ll-navy/80 max-w-md mx-auto leading-relaxed">
                Geen labels, geen types. Alleen dit: iets in jou reageerde. Dat is een vonk. Wat je ermee wilt doen, blijft aan jou.
              </p>

              <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/programma?programma=start"
                  data-testid="vonkvinder-cta-start"
                  className="group inline-flex items-center gap-4 bg-ll-ink text-ll-cream hover:bg-ll-coral rounded-full pl-7 pr-2 py-2 transition-all duration-300 justify-between"
                >
                  <span className="text-base font-medium">Ontdek START in 12 weken</span>
                  <span className="bg-ll-coral group-hover:bg-ll-yellow text-ll-ink w-11 h-11 rounded-full flex items-center justify-center">
                    <ArrowUpRight size={18} />
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={reset}
                  data-testid="vonkvinder-cta-opnieuw"
                  className="group inline-flex items-center gap-3 border-2 border-ll-ink text-ll-ink hover:bg-ll-yellow hover:border-ll-yellow rounded-full px-6 py-3 transition-all duration-300 justify-center"
                >
                  <Shuffle size={16} />
                  <span className="text-base font-medium">Trek een Vonk-kaart</span>
                </button>
              </div>

              <p className="mt-14 text-xs uppercase tracking-[0.28em] text-ll-navy/50">
                Deze ervaring blijft in jouw browser. We slaan niets op.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
